<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useInbox } from 'dashboard/composables/useInbox';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const { isAPersonalBridgeChannel } = useInbox();

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  id,
  conversationId,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

// Kept by the server when a message is deleted or edited in Telegram, so
// every operator can still read what it said before.
const originalContent = computed(
  () => contentAttributes.value?.originalContent || ''
);
const previousContents = computed(
  () => contentAttributes.value?.previousContents || []
);
const isEdited = computed(
  () => !!contentAttributes.value?.edited && !originalContent.value
);
const showHistory = ref(false);
const isDeleted = computed(() => !!contentAttributes.value?.deleted);

const historyToggleLabel = computed(() => {
  if (showHistory.value) return t('CONVERSATION.MESSAGE_HISTORY.HIDE');
  if (originalContent.value || isDeleted.value) {
    return t('CONVERSATION.MESSAGE_HISTORY.SHOW_ORIGINAL');
  }
  return t('CONVERSATION.MESSAGE_HISTORY.SHOW_PREVIOUS');
});

// Telegram files are not copied with imported history (and sometimes not
// with a live message, if the transfer failed): the text then starts with
// "📎 name", and the agent can fetch the file itself.
const canFetchTelegramFile = computed(
  () =>
    isAPersonalBridgeChannel.value &&
    !attachments.value?.length &&
    !isDeleted.value &&
    (!!contentAttributes.value?.telegramFile ||
      (content.value || '').startsWith('📎 '))
);
const isFetchingTelegramFile = ref(false);

const fetchTelegramFile = async () => {
  isFetchingTelegramFile.value = true;
  try {
    await store.dispatch('fetchTelegramFile', {
      conversationId: conversationId.value,
      messageId: id.value,
    });
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('CONVERSATION.TELEGRAM_FILE.FAILED')
    );
  } finally {
    isFetchingTelegramFile.value = false;
  }
};

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <div
        v-if="originalContent || isEdited || (isDeleted && attachments?.length)"
        class="-mt-2 flex flex-col gap-1 text-xs text-n-slate-11"
      >
        <div class="flex items-center gap-2">
          <span v-if="isEdited">{{
            $t('CONVERSATION.MESSAGE_HISTORY.EDITED')
          }}</span>
          <button
            v-if="originalContent || previousContents.length || isDeleted"
            type="button"
            class="underline"
            @click="showHistory = !showHistory"
          >
            {{ historyToggleLabel }}
          </button>
        </div>
        <template v-if="showHistory">
          <p
            v-for="(version, index) in originalContent
              ? [originalContent]
              : previousContents"
            :key="index"
            class="whitespace-pre-wrap rounded-md bg-n-alpha-1 px-2 py-1 line-through decoration-n-slate-9"
          >
            {{ version }}
          </p>
        </template>
      </div>
      <NextButton
        v-if="canFetchTelegramFile"
        faded
        slate
        xs
        icon="i-lucide-download"
        class="self-start"
        :is-loading="isFetchingTelegramFile"
        :label="$t('CONVERSATION.TELEGRAM_FILE.DOWNLOAD')"
        @click="fetchTelegramFile"
      />
      <!-- A deleted message's files stay, but behind the same toggle as its text. -->
      <AttachmentChips
        v-if="!isDeleted || showHistory"
        :attachments="attachments"
        class="gap-2"
      />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
